// The project's queue in detail, over the Board (queue/detail.rs, Poulpe-673):
// one line from the live store, and, once a person opens it, each open
// proposal's stage and since when, what the queue makes it wait on, its
// check's clocks against comparable runs, and its approval as its landing says
// it apart from the forecast. Every check on the project's runners shows in
// the line, and in the Checks tab (Poulpe-780), and the last hour in its own
// tab (Poulpe-781). The detail is read while the
// strip is on screen, every few seconds, and leaves the screen when a read
// fails or identity changes. Measurements, never evidence or an order.
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { errorMessage } from '../api/client'
import type { QueueCheck, QueueDetail, QueueItem } from '../api/types'
import { progressText } from '../checks/progress'
import { useSessionsEnv } from '../sessions/context'
import { useBoardState } from '../store/context'
import { ChecksLine, ChecksTab, LastHourTab } from './Checks'
import { summary } from './machineWords'
import { actualText, endedText, forecastText, historyText, PURPOSES, since, waitText } from './detail'
import { short, stepName } from './words'

/** How often an open detail is read again. */
export const QUEUE_DETAIL_POLL_MS = 5000

/** The steps, in the queue's order, as the line counts them. */
const STEPS = [['head', 'to GitHub'], ['check', 'checking'], ['review', 'in review'], ['queued', 'queued'], ['landing', 'landing']] as const


/** What was read, and for whom: shown only while it is still that project's and identity's. */
type Read = { identity: string; detail: QueueDetail | null; problem: string | null }

/** The project's queue detail, read every few seconds while `enabled`. */
export function useQueueDetail(project: string, enabled = true) {
  const { api } = useSessionsEnv()
  const { me, offline, status } = useBoardState()
  const member = me?.member?.id
  const identity = `${me?.workspace.id ?? ''}:${member ?? ''}:${project}`
  const read = api.queueDetail
  const readable = Boolean(read)
  // The reader as it is now: an API may hand a new function on each read of
  // it, and the poll, which runs while the strip shows, must not restart then.
  const reader = useRef(read)
  reader.current = read
  const [answer, setAnswer] = useState<Read | null>(null)
  useEffect(() => {
    setAnswer(null)
    if (!readable || !enabled || offline || status === 'signed_out') return
    const fetchDetail = (asked: string, as: string | undefined) => reader.current?.(asked, as) ?? Promise.reject(new Error('The queue detail is not available.'))
    let active = true
    let asking = false
    const ask = () => {
      if (asking || document.visibilityState === 'hidden') return
      asking = true
      fetchDetail(project, member).then(
        (detail) => { if (active) setAnswer({ identity, detail, problem: null }) },
        (error) => { if (active) setAnswer({ identity, detail: null, problem: errorMessage(error) }) },
      ).finally(() => { asking = false })
    }
    ask()
    const timer = setInterval(ask, QUEUE_DETAIL_POLL_MS)
    document.addEventListener('visibilitychange', ask)
    return () => { active = false; clearInterval(timer); document.removeEventListener('visibilitychange', ask) }
  }, [project, readable, enabled, member, identity, offline, status])
  // Late answers are dropped above; an answer for another identity is never shown, not even once.
  const current = answer?.identity === identity ? answer : null
  return { detail: current?.detail ?? null, problem: current?.problem ?? null, readable }
}

/**
 * The live clocks of a check, as the progress line says them, from the
 * server's time; its purpose first when the runner's report does not say it.
 */
function clocks(check: QueueCheck, measuredAt: string): string {
  // An ended job is never running, and its last report is not extended.
  if (check.state === 'finished' || check.state === 'refused') return endedText(check)
  const words = progressText({ job: check.job, project: '', session: null, proposal: null, landing: null, reported_at: check.reported_at, usual_ms: null, progress: check.progress }, Date.parse(measuredAt))
  return check.progress?.activity ? words : `${PURPOSES[check.purpose]}: ${words}`
}

function Item({ item, measuredAt }: { item: QueueItem; measuredAt: string }) {
  const key = item.issues[0]?.key
  const stage = item.landing?.state === 'waiting' ? 'Waiting to land' : item.landing?.state === 'deferred' ? 'Landing, deferred' : stepName(item.stage)
  const age = since(item.since, measuredAt)
  return <li className="queue-detail-item" data-stage={item.stage}>
    <div className="queue-detail-head">
      {key ? <Link to={`/${encodeURIComponent(key)}`} className="board-strip-key">{key}</Link> : <span className="board-strip-key">Proposal</span>}
      <code>{short(item.head)}</code>
      <span>{stage}{age && ` for ${age}`}</span>
      {item.priority !== 'none' && <span className="queue-detail-priority" title="The queue’s order does not read it">{item.priority}</span>}
    </div>
    <ul className="queue-detail-facts">
      <li>Waits on: {item.waits_on.map(waitText).join('; ')}</li>
      {item.check && <li>{clocks(item.check, measuredAt)}</li>}
      {item.check && <li>Comparable runs: {historyText(item.check.history)}</li>}
      {item.landing_history && <li>Landing checks on its runner: {historyText(item.landing_history)}</li>}
      <li>Approval: {actualText(item.approval.actual, measuredAt)} · {forecastText(item.approval.forecast)}</li>
      {item.blocked_by.length > 0 && <li className="muted">
        Blocked on the board by {item.blocked_by.map((issue) => issue.key).join(', ')}{item.blocked_by_total > item.blocked_by.length && ` and ${item.blocked_by_total - item.blocked_by.length} more`}: the queue does not wait on them
      </li>}
    </ul>
  </li>
}

/** How many checks run and wait on the project's runners. */
function checksCount(detail: QueueDetail): number {
  const said = summary(detail)
  return said.running + said.waiting
}

/** The project's open proposals and checks in one line, and their detail on demand. */
export function QueueDetailStrip({ project }: { project: string }) {
  const { proposals } = useBoardState()
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<'proposals' | 'checks' | 'hour'>('proposals')
  const body = useId()
  const steps = useMemo(() => {
    const count = new Map<string, number>()
    for (const proposal of proposals.values()) {
      if (proposal.project_id !== project || proposal.state !== 'open' || !proposal.step) continue
      count.set(proposal.step, (count.get(proposal.step) ?? 0) + 1)
    }
    return count
  }, [proposals, project])
  const { detail, problem, readable } = useQueueDetail(project)
  // Without the server's detail there is nothing to open; without a proposal
  // there may still be checks on its runners to see.
  if (!readable) return null
  const line = STEPS.flatMap(([step, words]) => steps.get(step) ? [`${steps.get(step)} ${words}`] : []).join(' · ') || 'No open proposals'
  return <section className="board-strip queue-detail" aria-label="Queue detail">
    <div className="board-strip-head">
      <span className="board-strip-flag">Queue</span>
      <span>{line}</span>
      {detail && <><span className="checks-sep" aria-hidden="true" /><ChecksLine detail={detail} /></>}
      <span className="checks-grow" />
      <button type="button" className="button button-small button-quiet" aria-expanded={open} aria-controls={body} onClick={() => setOpen(!open)}>
        {open ? 'Hide detail' : 'Show detail'}
      </button>
    </div>
    {open && <div id={body} className="queue-detail-body">
      {problem && <p role="alert" className="text-danger">{problem}</p>}
      {!problem && !detail && <p className="muted">Reading the queue…</p>}
      {detail && <>
        <div className="checks-tabs" role="tablist" aria-label="Queue detail">
          <button type="button" role="tab" aria-selected={tab === 'proposals'} className={tab === 'proposals' ? 'is-on' : undefined} onClick={() => setTab('proposals')}>
            Proposals <b>{detail.items_total}</b>
          </button>
          <button type="button" role="tab" aria-selected={tab === 'checks'} className={tab === 'checks' ? 'is-on' : undefined} onClick={() => setTab('checks')}>
            Checks <b>{checksCount(detail)}</b>
          </button>
          <button type="button" role="tab" aria-selected={tab === 'hour'} className={tab === 'hour' ? 'is-on' : undefined} onClick={() => setTab('hour')}>
            Last hour
          </button>
        </div>
        {tab === 'checks' && <ChecksTab detail={detail} />}
        {tab === 'hour' && <LastHourTab detail={detail} />}
      </>}
      {detail && tab === 'proposals' && <>
        {detail.items.length === 0 ? <p className="muted">No open proposals.</p>
          : <ol className="queue-detail-items" aria-label="Open proposals, oldest first">
            {detail.items.map((item) => <Item key={item.proposal} item={item} measuredAt={detail.measured_at} />)}
          </ol>}
        {detail.items_truncated && <p className="muted">Showing the oldest {detail.items.length} of {detail.items_total} proposals.</p>}
        {detail.active_checks.length > 0 && <>
          <h4 className="queue-detail-title">Checks on its runners</h4>
          <ul className="queue-detail-facts" aria-label="Checks on its runners">
            {detail.active_checks.map((check) => <li key={check.job}>
              {clocks(check, detail.measured_at)} · {historyText(check.history)}
            </li>)}
          </ul>
          {detail.active_truncated && <p className="muted">Showing the newest {detail.active_checks.length} of {detail.active_total} checks.</p>}
        </>}
        <p className="muted queue-detail-note">
          Times are measured, not promised. Warm and cold runs can mix in these times; a wide spread lowers confidence.
          {detail.history_truncated && ' History read only the most recent checks of its window.'}
        </p>
      </>}
    </div>}
  </section>
}
